import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { trafficLight, fmtEUR } from "../lib/format";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts";

const STATUS_COLORS = {
  red: "#EF4444",
  orange: "#F97316",
  yellow: "#EAB308",
  green: "#10B981",
  none: "#9CA3AF",
};
const STATUS_LABELS = { red: "Rosso <5g", orange: "Arancione 5-10g", yellow: "Giallo >10g", green: "Consegnata", none: "Senza scadenza" };

export default function AnalyticsCharts() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState({});

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/clients").then((r) => setClients(Object.fromEntries(r.data.map((c) => [c.id, c]))));
  }, []);

  // Status pie
  const statusCounts = { red: 0, orange: 0, yellow: 0, green: 0, none: 0 };
  projects.forEach((p) => { statusCounts[trafficLight(p.deadline, p.delivered).level]++; });
  const statusData = Object.entries(statusCounts).filter(([, v]) => v > 0).map(([k, v]) => ({ name: STATUS_LABELS[k], key: k, value: v }));

  // Amount by type (PA vs Privato) — split parcella + importo
  const byType = { pa: { name: "Pubblica Amm.", importo: 0, parcella: 0, count: 0 }, private: { name: "Privato", importo: 0, parcella: 0, count: 0 } };
  projects.forEach((p) => {
    const t = p.type === "pa" ? "pa" : "private";
    byType[t].importo += Number(p.importo_lavori || 0);
    byType[t].parcella += Number(p.parcella_calculated || 0);
    byType[t].count++;
  });
  const byTypeData = Object.values(byType);

  // Fatturazione stack (mat/fatt/pag totali)
  const finTotals = projects.reduce((a, p) => ({
    Maturato: a.Maturato + Number(p.total_accrued || 0),
    Fatturato: a.Fatturato + Number(p.total_invoiced || 0),
    Pagato: a.Pagato + Number(p.total_paid || 0),
  }), { Maturato: 0, Fatturato: 0, Pagato: 0 });
  const finData = Object.entries(finTotals).map(([name, value]) => ({ name, value }));
  const FIN_COLORS = ["#9CA3AF", "#3B82F6", "#10B981"];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="analytics-charts">
      {/* Status donut */}
      <div className="border border-neutral-200 bg-white p-5">
        <div className="mono-label">Semafori</div>
        <h3 className="font-display text-lg font-medium mt-1 mb-3">Distribuzione stato</h3>
        <div className="h-52">
          {statusData.length === 0 ? <div className="text-center text-sm text-neutral-500 py-10">Nessun dato</div> : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusData} dataKey="value" innerRadius={44} outerRadius={78} paddingAngle={2} stroke="#fff" strokeWidth={2}>
                  {statusData.map((d) => <Cell key={d.key} fill={STATUS_COLORS[d.key]} />)}
                </Pie>
                <Tooltip contentStyle={{ border: "1px solid #e5e5e5", borderRadius: 2, fontSize: 12, fontFamily: "IBM Plex Sans" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="mt-2 space-y-1">
          {statusData.map((d) => (
            <div key={d.key} className="flex items-center gap-2 text-xs">
              <div className="h-2.5 w-2.5" style={{ background: STATUS_COLORS[d.key] }} />
              <div className="flex-1">{d.name}</div>
              <div className="font-mono font-semibold">{d.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Type bars */}
      <div className="border border-neutral-200 bg-white p-5">
        <div className="mono-label">Ripartizione tipo</div>
        <h3 className="font-display text-lg font-medium mt-1 mb-3">Importo & parcella per tipologia</h3>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={byTypeData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#6b7280" }} tickLine={false} axisLine={{ stroke: "#e5e5e5" }} />
              <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#6b7280" }} tickLine={false} axisLine={{ stroke: "#e5e5e5" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ border: "1px solid #e5e5e5", borderRadius: 2, fontSize: 12 }} formatter={(v) => fmtEUR(v)} />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono", textTransform: "uppercase" }} />
              <Bar dataKey="importo" name="Importo lavori" fill="#9CA3AF" />
              <Bar dataKey="parcella" name="Parcella" fill="var(--brand-primary)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Financial stack */}
      <div className="border border-neutral-200 bg-white p-5">
        <div className="mono-label">Fatturazione</div>
        <h3 className="font-display text-lg font-medium mt-1 mb-3">Totali maturato/fatturato/pagato</h3>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={finData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#6b7280" }} tickLine={false} axisLine={{ stroke: "#e5e5e5" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#6b7280" }} tickLine={false} axisLine={{ stroke: "#e5e5e5" }} width={80} />
              <Tooltip contentStyle={{ border: "1px solid #e5e5e5", borderRadius: 2, fontSize: 12 }} formatter={(v) => fmtEUR(v)} />
              <Bar dataKey="value" name="Valore">
                {finData.map((d, i) => <Cell key={d.name} fill={FIN_COLORS[i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
